import { useEffect } from 'react';
import { css } from '@linaria/atomic';
import { useTranslation } from '@frontend/i18n';
import { RegisterTerminalModalStripeSteps } from '../../hooks';
import {
  RegisterTerminalModalStepProps,
  RegisterTerminalStep,
  RegisterTerminalStepModalWrapper,
} from '../../register-terminal-modal-step';
import { RegisterTerminalBody } from './register-terminal-body';
import { usePairS710Terminal } from './use-pair-s710-terminal';

const styles = {
  list: css`
    list-style-type: number;
    padding-left: var(--fabric-space-padding-md);
    margin-top: var(--fabric-space-gap-md);
    margin-bottom: var(--fabric-space-gap-xs);
    & > li:not(:first-of-type) {
      margin-top: var(--fabric-space-gap-md);
    }
    & > li:not(:last-of-type) {
      margin-bottom: var(--fabric-space-gap-md);
    }
  `,
};

export const SetupTerminal = (props: RegisterTerminalModalStepProps) => {
  const { t } = useTranslation('payments');

  return (
    <RegisterTerminalStepModalWrapper
      title={t('Set up and power on your terminal')}
      nextStep={RegisterTerminalModalStripeSteps.ConnectWifi}
      Action={RegisterTerminalStep.Next}
      {...props}
    >
      <ol className={styles.list}>
        <li>
          {t(
            'Charge the terminal in its cradle or with the provided USB-C power cable. Charge for 8 hours before first use.'
          )}
        </li>
        <li>{t('Hold the power button on the right side of the terminal until the screen turns on.')}</li>
        <li>{t('Allow any updates to finish before continuing.')}</li>
      </ol>
    </RegisterTerminalStepModalWrapper>
  );
};

export const ConnectWifi = (props: RegisterTerminalModalStepProps) => {
  const { t } = useTranslation('payments');

  return (
    <RegisterTerminalStepModalWrapper
      title={t('Connect your terminal to Wi-Fi')}
      nextStep={RegisterTerminalModalStripeSteps.RegisterTerminal}
      Action={RegisterTerminalStep.Next}
      {...props}
    >
      <ol className={styles.list}>
        <li>{t('Swipe right from the left edge of the terminal screen to open Settings.')}</li>
        <li>{t('Enter the admin PIN 07139.')}</li>
        <li>{t('Select Network, then WiFi.')}</li>
        <li>{t('Choose your WiFi network and enter the password.')}</li>
      </ol>
    </RegisterTerminalStepModalWrapper>
  );
};

type RegisterTerminalProps = RegisterTerminalModalStepProps<{
  onTerminalPaired: () => void;
  onSuccess: () => void;
  locationId: string | undefined;
  stripeLocationId: string | undefined;
  onStatusScreenChange?: (isStatusScreen: boolean) => void;
}>;
export const RegisterTerminal = ({
  onTerminalPaired,
  onSuccess,
  locationId,
  stripeLocationId,
  onStatusScreenChange,
  ...props
}: RegisterTerminalProps) => {
  const { t } = useTranslation('payments');

  const { handleRegisterTerminal, isComplete, isLoading, getFieldProps, showStatus, error, resetStatus } =
    usePairS710Terminal({
      onTerminalPaired,
      locationId,
      stripeLocationId,
    });

  const isStatusScreen = showStatus && !error;

  useEffect(() => {
    onStatusScreenChange?.(isStatusScreen);
    return () => onStatusScreenChange?.(false);
  }, [isStatusScreen, onStatusScreenChange]);

  return (
    <RegisterTerminalStepModalWrapper
      title={isStatusScreen ? '' : t('Register your terminal with Weave')}
      onClickAdvance={isStatusScreen ? onSuccess : handleRegisterTerminal}
      isAdvanceDisabled={!isStatusScreen && (!isComplete || isLoading)}
      Action={isStatusScreen ? RegisterTerminalStep.Complete : RegisterTerminalStep.Register}
      backLabel={isStatusScreen ? t('Register Another Terminal') : undefined}
      {...props}
      goBack={isStatusScreen ? resetStatus : props.goBack}
    >
      <RegisterTerminalBody
        listClassName={styles.list}
        isLoading={isLoading}
        getFieldProps={getFieldProps}
        error={error}
        showStatus={showStatus}
      />
    </RegisterTerminalStepModalWrapper>
  );
};
